import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

export interface PageMetadata {
  title: string;
  description: string;
  keywords?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogUrl?: string;
}

export const ROUTE_METADATA: Record<string, PageMetadata> = {
  '/': {
    title: 'Divine HMS - Enterprise Hospital Information System',
    description:
      'Divine Hospital Management System (HMS) - ABHA & ABDM compliant enabled intelligent Hospital ERP for OPD, IPD, Bed Management (ATD), Doctor Appointments, Billing, and Discharge Records.',
    keywords:
      'ABHA ABDM compliant enabled, ABHA, ABDM, Ayushman Bharat Digital Mission, Hospital Management System, HMS, Healthcare ERP, OPD Billing, IPD Management, Bed Management, ATD, Electronic Health Records, Doctor Appointments, Divine HMS, Devdeep Systems',
    ogTitle: 'Divine HMS - Intelligent Hospital Management System',
    ogDescription:
      'Streamline hospital operations with ABHA & ABDM compliant enabled Divine HMS: Real-time bed occupancy, sub-minute OPD billing, IPD tracking, and standardized digital medical records.',
    ogUrl: 'https://divine.devdeepsystems.com',
  },
  '/pharmacy': {
    title: 'Divine Pharmacy - Pharmacy ERP & Precision Stock Control',
    description:
      'Divine Pharmacy ERP - Complete retail medical & chemist software with POS counter billing, multi-batch expiry radar, inventory control, and statutory GST accounting.',
    keywords:
      'Pharmacy Management System, Pharmacy ERP, Chemist Software, Retail Medical Billing, Medicine Stock Inventory, Batch Expiry Tracking, GST Pharmacy Billing, Divine Pharmacy',
    ogTitle: 'Divine Pharmacy - Pharmacy ERP & Precision Stock Control',
    ogDescription:
      'Streamline pharmacy operations with Divine Pharmacy ERP: Rapid barcode POS billing, live batch & expiry tracking, automated purchase orders, and GST compliance.',
    ogUrl: 'https://divine.devdeepsystems.com/pharmacy',
  },
};

const updateMetaTag = (attributeName: string, attributeValue: string, content?: string) => {
  if (!content || typeof document === 'undefined') return;
  let element = document.querySelector(`meta[${attributeName}="${attributeValue}"]`);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attributeName, attributeValue);
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
};

export const applyPageMetadata = (meta: PageMetadata) => {
  if (typeof document === 'undefined') return;

  if (meta.title) {
    document.title = meta.title;
  }

  updateMetaTag('name', 'description', meta.description);
  if (meta.keywords) updateMetaTag('name', 'keywords', meta.keywords);
  if (meta.ogTitle) {
    updateMetaTag('property', 'og:title', meta.ogTitle);
    updateMetaTag('name', 'twitter:title', meta.ogTitle);
  }
  if (meta.ogDescription) {
    updateMetaTag('property', 'og:description', meta.ogDescription);
    updateMetaTag('name', 'twitter:description', meta.ogDescription);
  }
  if (meta.ogUrl) {
    updateMetaTag('property', 'og:url', meta.ogUrl);
  }
};

export function usePageMetadata(customMeta?: PageMetadata) {
  const { pathname } = useLocation();

  useEffect(() => {
    if (customMeta) {
      applyPageMetadata(customMeta);
      return;
    }

    if (pathname.startsWith('/pharmacy')) {
      applyPageMetadata(ROUTE_METADATA['/pharmacy']);
    } else {
      applyPageMetadata(ROUTE_METADATA['/']);
    }
  }, [pathname, customMeta]);
}
